function escapeRegExp(string: string): string {
  return string?.replace(/[\\^$.*+?()[```{}|]/g, '\\$&') ?? '';
}

const SPECIAL_CHARS_MAP: Record<string, string> = {
  '¥': '___yen___',
  // '€': '___euro___',
  // '£': '___pound___',
  // '©': '___copyright___',
  // '®': '___registered___',
};

export type SanitizeTitleFilter = (title: string, rawTitle: string, context: string) => string;

export function removeAccents(text: string, locale: string = '') {
  if (!/[\x80-\xff]/u.test(text)) {
    return text;
  }

  if (typeof String.prototype.normalize === 'function') {
    text = text.normalize('NFC');
  }

  const chars: { [key: string]: string } = {
    // Decompositions for Latin-1 Supplement.
    ª: 'a',
    º: 'o',
    À: 'A',
    Á: 'A',
    Â: 'A',
    Ã: 'A',
    Ä: 'A',
    Å: 'A',
    Æ: 'AE',
    Ç: 'C',
    È: 'E',
    É: 'E',
    Ê: 'E',
    Ë: 'E',
    Ì: 'I',
    Í: 'I',
    Î: 'I',
    Ï: 'I',
    Ð: 'D',
    Ñ: 'N',
    Ò: 'O',
    Ó: 'O',
    Ô: 'O',
    Õ: 'O',
    Ö: 'O',
    Ù: 'U',
    Ú: 'U',
    Û: 'U',
    Ü: 'U',
    Ý: 'Y',
    Þ: 'TH',
    ß: 's',
    à: 'a',
    á: 'a',
    â: 'a',
    ã: 'a',
    ä: 'a',
    å: 'a',
    æ: 'ae',
    ç: 'c',
    è: 'e',
    é: 'e',
    ê: 'e',
    ë: 'e',
    ì: 'i',
    í: 'i',
    î: 'i',
    ï: 'i',
    ð: 'd',
    ñ: 'n',
    ò: 'o',
    ó: 'o',
    ô: 'o',
    õ: 'o',
    ö: 'o',
    ø: 'o',
    ù: 'u',
    ú: 'u',
    û: 'u',
    ü: 'u',
    ý: 'y',
    þ: 'th',
    ÿ: 'y',
    Ø: 'O',
    // Decompositions for Latin Extended-A.
    Ā: 'A',
    ā: 'a',
    Ă: 'A',
    ă: 'a',
    Ą: 'A',
    ą: 'a',
    Ć: 'C',
    ć: 'c',
    Ĉ: 'C',
    ĉ: 'c',
    Ċ: 'C',
    ċ: 'c',
    Č: 'C',
    č: 'c',
    Ď: 'D',
    ď: 'd',
    Đ: 'D',
    đ: 'd',
    Ē: 'E',
    ē: 'e',
    Ĕ: 'E',
    ĕ: 'e',
    Ė: 'E',
    ė: 'e',
    Ę: 'E',
    ę: 'e',
    Ě: 'E',
    ě: 'e',
    Ĝ: 'G',
    ĝ: 'g',
    Ğ: 'G',
    ğ: 'g',
    Ġ: 'G',
    ġ: 'g',
    Ģ: 'G',
    ģ: 'g',
    Ĥ: 'H',
    ĥ: 'h',
    Ħ: 'H',
    ħ: 'h',
    Ĩ: 'I',
    ĩ: 'i',
    Ī: 'I',
    ī: 'i',
    Ĭ: 'I',
    ĭ: 'i',
    Į: 'I',
    į: 'i',
    İ: 'I',
    ı: 'i',
    Ĳ: 'IJ',
    ĳ: 'ij',
    Ĵ: 'J',
    ĵ: 'j',
    Ķ: 'K',
    ķ: 'k',
    ĸ: 'k',
    Ĺ: 'L',
    ĺ: 'l',
    Ļ: 'L',
    ļ: 'l',
    Ľ: 'L',
    ľ: 'l',
    Ŀ: 'L',
    ŀ: 'l',
    Ł: 'L',
    ł: 'l',
    Ń: 'N',
    ń: 'n',
    Ņ: 'N',
    ņ: 'n',
    Ň: 'N',
    ň: 'n',
    ŉ: 'n',
    Ŋ: 'N',
    ŋ: 'n',
    Ō: 'O',
    ō: 'o',
    Ŏ: 'O',
    ŏ: 'o',
    Ő: 'O',
    ő: 'o',
    Œ: 'OE',
    œ: 'oe',
    Ŕ: 'R',
    ŕ: 'r',
    Ŗ: 'R',
    ŗ: 'r',
    Ř: 'R',
    ř: 'r',
    Ś: 'S',
    ś: 's',
    Ŝ: 'S',
    ŝ: 's',
    Ş: 'S',
    ş: 's',
    Š: 'S',
    š: 's',
    Ţ: 'T',
    ţ: 't',
    Ť: 'T',
    ť: 't',
    Ŧ: 'T',
    ŧ: 't',
    Ũ: 'U',
    ũ: 'u',
    Ū: 'U',
    ū: 'u',
    Ŭ: 'U',
    ŭ: 'u',
    Ů: 'U',
    ů: 'u',
    Ű: 'U',
    ű: 'u',
    Ų: 'U',
    ų: 'u',
    Ŵ: 'W',
    ŵ: 'w',
    Ŷ: 'Y',
    ŷ: 'y',
    Ÿ: 'Y',
    Ź: 'Z',
    ź: 'z',
    Ż: 'Z',
    ż: 'z',
    Ž: 'Z',
    ž: 'z',
    ſ: 's',
    // Decompositions for Latin Extended-B.
    Ə: 'E',
    ǝ: 'e',
    Ș: 'S',
    ș: 's',
    Ț: 'T',
    ț: 't',
    // Euro sign.
    '€': 'E',
    // GBP (Pound) sign.
    '£': '',
    // Vowels with diacritic (Vietnamese). Unmarked.
    Ơ: 'O',
    ơ: 'o',
    Ư: 'U',
    ư: 'u',
    // Grave accent.
    Ầ: 'A',
    ầ: 'a',
    Ằ: 'A',
    ằ: 'a',
    Ề: 'E',
    ề: 'e',
    Ồ: 'O',
    ồ: 'o',
    Ờ: 'O',
    ờ: 'o',
    Ừ: 'U',
    ừ: 'u',
    Ỳ: 'Y',
    ỳ: 'y',
    // Hook.
    Ả: 'A',
    ả: 'a',
    Ẩ: 'A',
    ẩ: 'a',
    Ẳ: 'A',
    ẳ: 'a',
    Ẻ: 'E',
    ẻ: 'e',
    Ể: 'E',
    ể: 'e',
    Ỉ: 'I',
    ỉ: 'i',
    Ỏ: 'O',
    ỏ: 'o',
    Ổ: 'O',
    ổ: 'o',
    Ở: 'O',
    ở: 'o',
    Ủ: 'U',
    ủ: 'u',
    Ử: 'U',
    ử: 'u',
    Ỷ: 'Y',
    ỷ: 'y',
    // Tilde.
    Ẫ: 'A',
    ẫ: 'a',
    Ẵ: 'A',
    ẵ: 'a',
    Ẽ: 'E',
    ẽ: 'e',
    Ễ: 'E',
    ễ: 'e',
    Ỗ: 'O',
    ỗ: 'o',
    Ỡ: 'O',
    ỡ: 'o',
    Ữ: 'U',
    ữ: 'u',
    Ỹ: 'Y',
    ỹ: 'y',
    // Acute accent.
    Ấ: 'A',
    ấ: 'a',
    Ắ: 'A',
    ắ: 'a',
    Ế: 'E',
    ế: 'e',
    Ố: 'O',
    ố: 'o',
    Ớ: 'O',
    ớ: 'o',
    Ứ: 'U',
    ứ: 'u',
    // Dot below.
    Ạ: 'A',
    ạ: 'a',
    Ậ: 'A',
    ậ: 'a',
    Ặ: 'A',
    ặ: 'a',
    Ẹ: 'E',
    ẹ: 'e',
    Ệ: 'E',
    ệ: 'e',
    Ị: 'I',
    ị: 'i',
    Ọ: 'O',
    ọ: 'o',
    Ộ: 'O',
    ộ: 'o',
    Ợ: 'O',
    ợ: 'o',
    Ụ: 'U',
    ụ: 'u',
    Ự: 'U',
    ự: 'u',
    Ỵ: 'Y',
    ỵ: 'y',
    // Vowels with diacritic (Chinese, Hanyu Pinyin).
    ɑ: 'a',
    // Macron.
    Ǖ: 'U',
    ǖ: 'u',
    // Acute accent.
    Ǘ: 'U',
    ǘ: 'u',
    // Caron.
    Ǎ: 'A',
    ǎ: 'a',
    Ǐ: 'I',
    ǐ: 'i',
    Ǒ: 'O',
    ǒ: 'o',
    Ǔ: 'U',
    ǔ: 'u',
    Ǚ: 'U',
    ǚ: 'u',
    // Grave accent.
    Ǜ: 'U',
    ǜ: 'u',
  };

  // Locale-specific rules
  if (locale.startsWith('de')) {
    chars['Ä'] = 'Ae';
    chars['ä'] = 'ae';
    chars['Ö'] = 'Oe';
    chars['ö'] = 'oe';
    chars['Ü'] = 'Ue';
    chars['ü'] = 'ue';
    chars['ß'] = 'ss';
  } else if (locale === 'da_DK') {
    chars['Æ'] = 'Ae';
    chars['æ'] = 'ae';
    chars['Ø'] = 'Oe';
    chars['ø'] = 'oe';
    chars['Å'] = 'Aa';
    chars['å'] = 'aa';
  } else if (locale === 'ca') {
    chars['l·l'] = 'll';
  } else if (locale === 'sr_RS' || locale === 'bs_BA') {
    chars['Đ'] = 'DJ';
    chars['đ'] = 'dj';
  }

  // Replace characters
  let result = text;
  for (const [search, replace] of Object.entries(chars)) {
    result = result.split(search).join(replace);
  }

  return result;
}

/**
 * Sanitizes a title, replacing whitespace and special characters with dashes.
 * WordPress equivalent of sanitize_title_with_dashes()
 *
 * @param title - The title to sanitize
 * @param rawTitle - The original title before any processing
 * @param context - The context for which the title is being sanitized ('save' or 'display')
 * @returns The sanitized title
 */
export function sanitizeTitleWithDashes(title: string, rawTitle: string = '', context: string = 'display'): string {
  // Remove HTML tags
  title = title.replace(/<[^>]*>/g, '');

  // Remove accents
  title = removeAccents(title);

  if (context === 'save') {
    // Convert &nbsp; and other entities to space
    title = title.replace(/&nbsp;/g, ' ');

    // Convert to lowercase
    title = title.toLowerCase();

    // Replace whitespace with dashes
    title = title.replace(/\s+/g, '-');

    // 🔥 Remove all characters except letters, numbers, underscores, percent signs, and dashes
    title = title.replace(/[^a-z0-9_%\-]/g, '');

    // Replace multiple dashes with single dash
    title = title.replace(/-+/g, '-');

    // Trim dashes from the beginning and end
    title = title.replace(/^-+|-+$/g, '');
  }

  return title;
}

/**
 * Sanitizes a title string.
 * WordPress equivalent of sanitize_title()
 *
 * @param title - The title to sanitize
 * @param fallbackTitle - Fallback title if the result is empty
 * @param context - The context for sanitization ('save' or 'display')
 * @param filterCallback - Optional custom filter callback
 * @returns The sanitized title
 */
export function sanitizeTitle(title: string, fallbackTitle: string = '', context: string = 'save', filterCallback?: SanitizeTitleFilter): string {
  const rawTitle = title;

  if (context === 'save') {
    title = removeAccents(title);
  }

  if (filterCallback) {
    title = filterCallback(title, rawTitle, context);
  } else {
    title = sanitizeTitleWithDashes(title, rawTitle, context);
  }

  if (title === '' || !title) {
    title = fallbackTitle;
  }

  return title;
}

export const keepSpecialFilter: SanitizeTitleFilter = (title, rawTitle, context) => {
  if (!title) {
    title = rawTitle ?? '';
  }

  Object.entries(SPECIAL_CHARS_MAP).forEach(([char, placeholder]) => {
    title = title.replace(new RegExp(escapeRegExp(char), 'g'), placeholder);
  });

  title = sanitizeTitleWithDashes(title, rawTitle, context);

  Object.entries(SPECIAL_CHARS_MAP).forEach(([char, placeholder]) => {
    title = title.replace(new RegExp(placeholder, 'g'), char);
  });

  return title;
};
